import { useState, useCallback, useRef } from "react";
import { Search, SearchX, X, Plus } from "lucide-react";
import { IPlayerDetail } from "../../models/IPlayer";
import { ITeamPlayer } from "../../models/IMatch";
import { searchPlayers } from "../../api/players";
import { updateTeam } from "../../api/tournaments";
import { Alert, Avatar, Button, Dialog, EmptyState, IconButton, Input, Loader, SearchInput, useDialogs } from "../../ui";
import "./styles.scss";

type UnregisteredDraft = { localId: string; name: string };

type AddTeamMemberDialogProps = {
  teamId: string;
  currentMembers: IPlayerDetail[];
  maxPlayers: number;
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const AddTeamMemberDialog = (props: AddTeamMemberDialogProps) => {
  const { alert } = useDialogs();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [players, setPlayers] = useState<ITeamPlayer[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<ITeamPlayer[]>([]);
  const [unregisteredDrafts, setUnregisteredDrafts] = useState<UnregisteredDraft[]>([]);
  const [showNewPlayer, setShowNewPlayer] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Total roster = existing members + newly-selected registered + newly-added
  // non-registered. This must not exceed the category's max players per team.
  // maxPlayers <= 0 means "no limit configured".
  const existingCount = props.currentMembers.length;
  const totalCount = existingCount + selectedPlayers.length + unregisteredDrafts.length;
  const isFull = props.maxPlayers > 0 && totalCount >= props.maxPlayers;

  const fetchPlayers = useCallback(
    async (search: string) => {
      if (!search.trim()) {
        setPlayers([]);
        return;
      }

      setLoading(true);
      try {
        const { data } = await searchPlayers(search);
        // Map the API response to add playerId field (API returns "id" but we need "playerId")
        const mappedPlayers = data.map((p: any) => ({
          ...p,
          playerId: p.id, // Map id to playerId
        }));
        // Only filter out players already in the team
        const currentMemberIds = props.currentMembers.map((m) => m.playerId);
        const availablePlayers = mappedPlayers.filter((p) => !currentMemberIds.includes(p.playerId));
        setPlayers(availablePlayers);
      } catch (error) {
        console.error("Error fetching players:", error);
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    },
    [props.currentMembers]
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchPlayers(value);
      }, 500);
    },
    [fetchPlayers]
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Clear old results when search term changes
    if (value.trim() !== searchTerm.trim()) {
      setPlayers([]);
    }
    debouncedSearch(value);
  };

  // The kit Dialog plays the exit animation itself: reset the picker and hand the close to the parent right away.
  const handleClose = () => {
    setSelectedPlayers([]);
    setUnregisteredDrafts([]);
    setShowNewPlayer(false);
    setNewFirstName("");
    setNewLastName("");
    setSearchTerm("");
    setPlayers([]);
    setLimitError(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    props.onClose();
  };

  const handleAddUnregisteredPlayer = () => {
    const name = `${newFirstName.trim()} ${newLastName.trim()}`.trim();
    if (!name) return;
    if (isFull) {
      setLimitError(`This team is limited to ${props.maxPlayers} players.`);
      return;
    }
    setUnregisteredDrafts((prev) => [...prev, { localId: `unregistered_${Date.now()}`, name }]);
    setNewFirstName("");
    setNewLastName("");
    setShowNewPlayer(false);
  };

  const handleRemoveUnregistered = (localId: string) => {
    setUnregisteredDrafts((prev) => prev.filter((d) => d.localId !== localId));
    setLimitError(null);
  };

  const handleSelectPlayer = (player: ITeamPlayer) => {
    if (isFull) {
      setLimitError(`This team is limited to ${props.maxPlayers} players.`);
      return;
    }
    setSelectedPlayers((prev) => [...prev, player]);
  };

  const handleRemovePlayer = (player: ITeamPlayer) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.playerId !== player.playerId));
    setLimitError(null);
  };

  // Filter out selected players from search results
  const visiblePlayers = players.filter(
    (player) => !selectedPlayers.some((selected) => selected.playerId === player.playerId)
  );

  const handleConfirm = async () => {
    if (selectedPlayers.length === 0 && unregisteredDrafts.length === 0) {
      handleClose();
      return;
    }

    setSubmitting(true);
    try {
      const existingMembers = props.currentMembers.map((member) =>
        member.playerId
          ? { teamMemberId: member.teamMemberId, playerId: member.playerId, consent: member.consent }
          : { teamMemberId: member.teamMemberId, consent: member.consent }
      );

      const newMembers = selectedPlayers.map((player) => ({
        playerId: player.id,
        consent: true,
      }));

      const newUnregistered = unregisteredDrafts.map((d) => ({
        playerName: d.name,
        consent: true,
      }));

      await updateTeam(props.teamId, {
        teamMembers: [...existingMembers, ...newMembers, ...newUnregistered],
      });

      props.onConfirm();
      handleClose();
    } catch (error: any) {
      console.error("Error adding team members:", error);
      await alert(error.message || "Failed to add team members");
    } finally {
      setSubmitting(false);
    }
  };

  const pickedCount = selectedPlayers.length + unregisteredDrafts.length;

  const playerDetails = (player: ITeamPlayer) => (
    <span className="add-team-member__details">
      <span>{player.nationality}</span>
      {player.isCaptain && (
        <>
          <span className="add-team-member__separator">•</span>
          <span className="add-team-member__captain">Captain</span>
        </>
      )}
    </span>
  );

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title="Add Team Members"
      size="md"
      busy={submitting}
      bodyClassName="add-team-member"
      footer={
        <div className="add-team-member__foot">
          {!showNewPlayer ? (
            <Button
              variant="tint"
              block
              icon={<Plus />}
              onClick={() => {
                if (isFull) {
                  setLimitError(`This team is limited to ${props.maxPlayers} players.`);
                  return;
                }
                setShowNewPlayer(true);
              }}
              disabled={isFull}
            >
              Add non-registered player
            </Button>
          ) : (
            <div className="add-team-member__new-player">
              <Input
                placeholder="First name *"
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
                autoFocus
              />
              <Input
                placeholder="Last name"
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
              />
              <Button variant="primary" disabled={!newFirstName.trim()} onClick={handleAddUnregisteredPlayer}>
                Add
              </Button>
              <IconButton
                label="Cancel"
                icon={<X />}
                onClick={() => {
                  setShowNewPlayer(false);
                  setNewFirstName("");
                  setNewLastName("");
                }}
              />
            </div>
          )}
          <div className="add-team-member__actions">
            <Button variant="ghost" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={submitting}
              disabled={selectedPlayers.length === 0 && unregisteredDrafts.length === 0}
            >
              {submitting
                ? "Adding..."
                : `Add ${(selectedPlayers.length + unregisteredDrafts.length) || ""} Player${selectedPlayers.length + unregisteredDrafts.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="add-team-member__search">
        <SearchInput
          block
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={handleSearch}
          autoFocus
        />
      </div>

      <div className="add-team-member__content">
        {props.maxPlayers > 0 && (
          <div className="add-team-member__size">
            <span>
              Team size:{" "}
              <strong className={isFull ? "is-full" : undefined}>
                {totalCount}/{props.maxPlayers}
              </strong>{" "}
              players
            </span>
            {existingCount > 0 && (
              <span className="add-team-member__existing">{existingCount} already on team</span>
            )}
          </div>
        )}
        {limitError && <Alert tone="danger">{limitError}</Alert>}
        {pickedCount > 0 && (
          <section className="add-team-member__selected">
            <p className="add-team-member__section-title add-team-member__section-title--spark">
              Selected Players ({selectedPlayers.length + unregisteredDrafts.length})
            </p>
            <ul className="add-team-member__list">
              {unregisteredDrafts.map((draft) => (
                <li key={draft.localId} className="add-team-member__row add-team-member__row--selected">
                  <Avatar name={draft.name} size={32} />
                  <span className="add-team-member__info">
                    <span className="add-team-member__name">{draft.name}</span>
                    <span className="add-team-member__details">
                      <span>Non-registered</span>
                    </span>
                  </span>
                  <IconButton
                    label="Remove player"
                    icon={<X />}
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveUnregistered(draft.localId)}
                  />
                </li>
              ))}
              {selectedPlayers.map((player) => (
                <li key={player.playerId} className="add-team-member__row add-team-member__row--selected">
                  <Avatar name={`${player.firstName} ${player.lastName || ""}`} size={32} />
                  <span className="add-team-member__info">
                    <span className="add-team-member__name">
                      {player.firstName} {player.lastName || ""}
                    </span>
                    {playerDetails(player)}
                  </span>
                  <IconButton
                    label="Remove player"
                    icon={<X />}
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemovePlayer(player)}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {!searchTerm.trim() ? (
          <EmptyState compact icon={<Search />} title="Type to search for players by full name, nickname or email" />
        ) : loading ? (
          <Loader label="Searching..." minHeight={140} />
        ) : visiblePlayers.length === 0 ? (
          <EmptyState compact icon={<SearchX />} title="No players found matching your search" />
        ) : (
          <section>
            <p className="add-team-member__section-title">Search Results</p>
            <ul className="add-team-member__list">
              {visiblePlayers.map((player) => (
                <li key={player.playerId}>
                  <button
                    type="button"
                    className={`add-team-member__row add-team-member__pick${isFull ? " is-disabled" : ""}`}
                    aria-disabled={isFull || undefined}
                    onClick={() => handleSelectPlayer(player)}
                  >
                    <Avatar name={`${player.firstName} ${player.lastName || ""}`} size={32} />
                    <span className="add-team-member__info">
                      <span className="add-team-member__name">
                        {player.firstName} {player.lastName || ""}
                      </span>
                      {playerDetails(player)}
                    </span>
                    <Plus className="add-team-member__pick-icon" size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
